import { FormControl, FormField as FormFieldRoot, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { cn } from "@/lib/utils";

interface FormFieldProps {
  form: UseFormReturn<any>;
  name: string;
  label: string;
  type?: "text" | "email" | "tel" | "date" | "select" | "textarea";
  placeholder?: string;
  options?: { value: string; label: string; }[];
  required?: boolean;
  description?: string;
}

export const FormField = ({ 
  form, 
  name, 
  label, 
  type = "text", 
  placeholder, 
  options,
  required,
  description 
}: FormFieldProps) => {
  const fieldId = `field-${name}`;
  const descriptionId = description ? `${fieldId}-description` : undefined;
  const hasError = !!form.formState.errors[name];
  const errorMessage = form.formState.errors[name]?.message as string;

  return (
    <FormFieldRoot
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel 
            className={cn(
              "text-sm font-medium text-gray-700",
              hasError && "text-red-500"
            )} 
            htmlFor={fieldId}
            aria-required={required}
          >
            {label}
            {required && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
          </FormLabel>
          <FormControl>
            {type === "select" && options ? (
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                aria-label={label}
                aria-required={required}
                aria-invalid={hasError}
                aria-describedby={cn(
                  descriptionId,
                  hasError && `${fieldId}-error`
                )}
              >
                <SelectTrigger 
                  id={fieldId} 
                  className={cn(
                    hasError && "border-red-500 focus:ring-red-500"
                  )}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      role="option"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : type === "textarea" ? (
              <Textarea
                {...field}
                id={fieldId}
                placeholder={placeholder}
                className={cn(
                  "min-h-[80px]",
                  hasError && "border-red-500 focus:ring-red-500"
                )}
                aria-label={label}
                aria-required={required}
                aria-invalid={hasError}
                aria-describedby={cn(
                  descriptionId,
                  hasError && `${fieldId}-error`
                )}
                onBlur={() => form.trigger(name)}
              />
            ) : (
              <Input
                {...field}
                id={fieldId}
                type={type}
                placeholder={placeholder}
                className={cn(
                  hasError && "border-red-500 focus:ring-red-500"
                )}
                aria-label={label}
                aria-required={required}
                aria-invalid={hasError}
                aria-describedby={cn(
                  descriptionId,
                  hasError && `${fieldId}-error`
                )}
                onBlur={() => form.trigger(name)}
              />
            )}
          </FormControl>
          {description && (
            <p 
              id={descriptionId} 
              className="text-sm text-gray-500 mt-1"
            >
              {description}
            </p>
          )}
          <FormMessage 
            id={`${fieldId}-error`}
            role="alert"
            aria-live="polite"
            className="text-sm text-red-500 mt-1 animate-fade-in" 
          />
        </FormItem>
      )}
    />
  );
};